import { Button } from "@/components/ui/baseUi/button"
import { Chip } from "@/components/ui/baseUi/chip"
import { ChipGroup } from "@/components/ui/baseUi/chip-groupe"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { DiscountInput } from "@/components/ui/forms/discountInput"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxShopSalesDialog } from "@/modules/boxShop/components/boxShopSalesDialog"
import { BoxShopShoppingListItem } from "@/modules/boxShop/components/boxShopShoppingListItem"
import { BoxShopShoppingListItemSkeleton } from "@/modules/boxShop/components/boxShopShoppingListItemSkeleton"
import type { BoxShopItemEntity } from "@/modules/boxShop/entities/boxShopItemEntity"
import { BOX_SHOP_SALES_MAX_QUANTITY } from "@/modules/boxShop/entities/boxShopSalesEntity"
import { BoxShopSalesPayement, type BoxShopSalesFormValues } from "@/modules/boxShop/form/boxShopFormik"
import { boxShopSalesFormSchema } from "@/modules/boxShop/form/boxShopSalesFormSchema"
import { BoxShopItemCategory } from "@/modules/boxShop/gateway/boxShopApiDto"
import type { BoxShopSalesCouponDto } from "@/modules/boxShop/gateway/boxShopSalesApiDto"
import { useBoxShopSalesCreate } from "@/modules/boxShop/hooks/useBoxShopSales"
import type { BoxShopOrderItem } from "@/modules/boxShop/type/boxShopType"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { CouponType } from "@/modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@/modules/core/utils/i18n"
import { formatCurrency } from "@/modules/currency/currency"
import { OutOfBandTransactionStatus } from "@/modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import useDebounce from "@/modules/hooks/useDebounce"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import boxShopShoppingVector from "@images/illustrations/boxShopShoppingVector.svg"
import { useStore } from "@tanstack/react-form"
import {
  CookieIcon,
  CupSodaIcon,
  LoaderCircleIcon,
  MinusIcon,
  PlusIcon,
  ScanQrCodeIcon,
  SearchIcon,
  ShapesIcon,
  ShirtIcon,
  StoreIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react"
import { Activity, Fragment, Suspense, useEffect, useEffectEvent, useState } from "react"

export function BoxShopShoppingView() {
  const { result: currentBox } = useCurrentBox()
  const language = useLanguage()
  const [selectedCategory, setSelectedCategory] = useState<BoxShopItemCategory | undefined>(undefined)
  const [newOrder, setNewOrder] = useState<BoxShopOrderItem[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const searchTermDebounced = useDebounce(searchTerm, 300)
  const [saleId, setSaleId] = useState<string | undefined>(undefined)
  const { createSale, isPendingCreateSale } = useBoxShopSalesCreate()
  const canSaleItem = useBoxStaffPermission(BoxStaffPermissions.SALE_ITEM)
  const [salesDialogOpen, setSalesDialogOpen] = useState(false)
  const [salesDialogCheckoutUrl, setSalesDialogCheckoutUrl] = useState<string | undefined>(undefined)
  const [VAT, setVAT] = useState(0)
  const [totalPrice, setTotalPrice] = useState(0)

  const subtotal = newOrder.reduce((acc, orderItem) => acc + orderItem.product.price.value * orderItem.quantity, 0)

  const defaultValues: BoxShopSalesFormValues = {
    payment: BoxShopSalesPayement.DIRECT,
    outOfBand: null,
    items: [],
    coupon: null,
    userId: null,
  }

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: boxShopSalesFormSchema(subtotal),
      onChange: boxShopSalesFormSchema(subtotal),
      onBlur: boxShopSalesFormSchema(subtotal),
    },
    onSubmit: async ({ value }) => {
      await createSale(value)
        .then((sale) => {
          setSaleId(sale.id)
          setSalesDialogOpen(true)
          setSalesDialogCheckoutUrl(sale.checkoutUrl ?? undefined)
          toast.success(word("boxShop.shoppingView.newOrder.toast.success"), {
            classNames: successToastClassName(),
          })
        })
        .catch(() => {
          toast.error(word("boxShop.shoppingView.newOrder.toast.error"), {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  // Reactive reads used outside of fields (VAT/total computation and the sales dialog props).
  const couponValue = useStore(form.baseStore, (state) => state.values.coupon)
  const paymentValue = useStore(form.baseStore, (state) => state.values.payment)
  const userIdValue = useStore(form.baseStore, (state) => state.values.userId)

  function clearSearch() {
    setSearchTerm("")
  }

  function handleAddItem(item: BoxShopItemEntity) {
    setNewOrder((prevOrder) => {
      const existingIndex = prevOrder.findIndex((orderItem) => orderItem.product.id === item.id)
      let updatedOrder

      if (existingIndex > -1) {
        const currentQuantity = prevOrder[existingIndex]?.quantity ?? 0
        const nextQuantity = Math.min(currentQuantity + 1, BOX_SHOP_SALES_MAX_QUANTITY)
        updatedOrder = prevOrder.map((orderItem, idx) =>
          idx === existingIndex
            ? {
                ...orderItem,
                quantity: nextQuantity,
              }
            : orderItem,
        )
      } else {
        updatedOrder = [...prevOrder, { product: item, quantity: 1 }]
      }

      return updatedOrder
    })
  }

  function handleRemoveItem(item: BoxShopOrderItem) {
    setNewOrder((prevOrder) => {
      return prevOrder.filter((orderItem) => orderItem.product.id !== item.product.id)
    })
  }

  function handleSubtractItem(item: BoxShopOrderItem) {
    setNewOrder((prevOrder) => {
      return prevOrder.map((orderItem) =>
        orderItem.product.id === item.product.id ? { ...orderItem, quantity: orderItem.quantity - 1 } : orderItem,
      )
    })
  }

  function handleResetOrder() {
    setNewOrder([])
    form.reset()
  }

  const onVATChange = useEffectEvent(() => {
    setVAT(() => {
      const coupon = couponValue
      return newOrder.reduce((acc: number, orderItem: BoxShopOrderItem) => {
        const quantity = orderItem.quantity
        const priceTTC = orderItem.product.price.value
        const taxAmount = orderItem.product.taxAmount ?? 0
        const priceHT = priceTTC - taxAmount

        // Apply discount to HT (unit)
        let discountedPriceHT = priceHT
        if (coupon && !isNaN(coupon.value)) {
          if (coupon.type === CouponType.PERCENT) {
            discountedPriceHT = priceHT * (1 - coupon.value / 100)
          } else {
            // Repartition the DIRECT discount proportionally to subtotal
            const subtotal = newOrder.reduce(
              (sum, item) => sum + (item.product.price.value - (item.product.taxAmount ?? 0)) * item.quantity,
              0,
            )
            // Safety: avoid division by zero
            if (subtotal > 0) {
              const thisDiscount = ((priceHT * quantity) / subtotal) * coupon.value
              discountedPriceHT = Math.max(0, priceHT - thisDiscount / quantity)
            }
          }
        }

        // Recalculate tax for discounted HT
        // Tax rate per item (taxAmount over priceHT)
        const taxRate = priceHT > 0 ? taxAmount / priceHT : 0
        const itemVAT = discountedPriceHT * taxRate * quantity

        return acc + itemVAT
      }, 0)
    })
  })

  const onTotalPriceChange = useEffectEvent(() => {
    setTotalPrice(() => {
      const coupon = couponValue
      if (!coupon || isNaN(coupon.value)) return subtotal
      if (coupon.type === CouponType.DIRECT) {
        return Math.max(0, subtotal - coupon.value)
      }
      return subtotal - subtotal * (coupon.value / 100)
    })
  })

  // Keep the form 'items' in sync with the order built in local state.
  useEffect(() => {
    form.setFieldValue(
      "items",
      newOrder.map((orderItem) => ({
        id: orderItem.product.id,
        quantity: orderItem.quantity,
      })),
    )
  }, [form, newOrder])

  useEffect(() => {
    onVATChange()
    onTotalPriceChange()
  }, [newOrder, couponValue])

  return (
    <>
      <div className="flex flex-1 flex-row gap-4">
        {/* PRODUCTS LIST */}
        <div className="flex flex-1 flex-col gap-4">
          {/* SEARCH INPUT */}
          <InputGroup className="h-12">
            <InputGroupInput
              placeholder={word("boxShop.shoppingView.search.placeholder")}
              onChange={(e) => {
                setSearchTerm(e.currentTarget.value)
              }}
              value={searchTerm}
            />
            <InputGroupAddon align="inline-start">
              <SearchIcon />
            </InputGroupAddon>
            {searchTerm && (
              <InputGroupAddon align="inline-end">
                <XIcon className="size-4 cursor-pointer" onClick={clearSearch} />
              </InputGroupAddon>
            )}
          </InputGroup>

          <ChipGroup spacing={2} variant="emphasisLow" value={selectedCategory ? [selectedCategory] : []}>
            <Chip
              key={BoxShopItemCategory.SNACK}
              value={BoxShopItemCategory.SNACK}
              variant="emphasisLow"
              size="md"
              aria-label="Toggle snacks"
              aria-pressed={selectedCategory === BoxShopItemCategory.SNACK}
              onClick={() => {
                setSelectedCategory(
                  selectedCategory === BoxShopItemCategory.SNACK ? undefined : BoxShopItemCategory.SNACK,
                )
              }}
            >
              <CookieIcon />
              {word("boxShop.shoppingView.categories.snacks")}
            </Chip>
            <Chip
              key={BoxShopItemCategory.BEVERAGE}
              value={BoxShopItemCategory.BEVERAGE}
              variant="emphasisLow"
              size="md"
              aria-label="Toggle beverages"
              aria-pressed={selectedCategory === BoxShopItemCategory.BEVERAGE}
              onClick={() => {
                setSelectedCategory(
                  selectedCategory === BoxShopItemCategory.BEVERAGE ? undefined : BoxShopItemCategory.BEVERAGE,
                )
              }}
            >
              <CupSodaIcon />
              {word("boxShop.shoppingView.categories.beverages")}
            </Chip>
            <Chip
              key={BoxShopItemCategory.CLOTHING}
              value={BoxShopItemCategory.CLOTHING}
              variant="emphasisLow"
              size="md"
              aria-label="Toggle clothing"
              aria-pressed={selectedCategory === BoxShopItemCategory.CLOTHING}
              onClick={() => {
                setSelectedCategory(
                  selectedCategory === BoxShopItemCategory.CLOTHING ? undefined : BoxShopItemCategory.CLOTHING,
                )
              }}
            >
              <ShirtIcon />
              {word("boxShop.shoppingView.categories.clothes")}
            </Chip>
            <Chip
              key={BoxShopItemCategory.ACCESSORY}
              value={BoxShopItemCategory.ACCESSORY}
              variant="emphasisLow"
              size="md"
              aria-label="Toggle accessories"
              aria-pressed={selectedCategory === BoxShopItemCategory.ACCESSORY}
              onClick={() => {
                setSelectedCategory(
                  selectedCategory === BoxShopItemCategory.ACCESSORY ? undefined : BoxShopItemCategory.ACCESSORY,
                )
              }}
            >
              <ShapesIcon />
              {word("boxShop.shoppingView.categories.accessories")}
            </Chip>
          </ChipGroup>

          <Suspense fallback={<BoxShopShoppingListItemSkeleton />}>
            <BoxShopShoppingListItem
              onAddItem={handleAddItem}
              category={selectedCategory}
              searchTerm={searchTermDebounced}
            />
          </Suspense>
        </div>

        {/* NEW ORDER */}
        <div className="relative -mt-15 flex h-fit min-h-[70vh] w-120 min-w-md flex-col gap-4 bg-linear-to-b from-gray-900 from-0% via-gray-900 via-3% to-interactive-background-neutral-default to-100% pr-4 pb-8 pl-6">
          {/* NEW ORDER VECTOR */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 w-full bg-auto bg-top bg-repeat-x"
            style={{ backgroundImage: `url(${boxShopShoppingVector})` }}
          />
          {/* NEW ORDER TITLE */}
          <Text fontStyle="HS" className="mt-7 font-spaceMono!">
            {word("boxShop.shoppingView.newOrder.header")}
          </Text>

          {/* NEW ORDER TABLE */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6">
              <Text fontStyle="BM" className="font-spaceMono!">
                {word("boxShop.shoppingView.newOrder.table.header.itemName")}
              </Text>
            </div>
            <div className="col-span-2 flex">
              <Text fontStyle="BM" className="w-full text-center font-spaceMono!">
                {word("boxShop.shoppingView.newOrder.table.header.quantity")}
              </Text>
            </div>
            <div className="col-span-4 flex">
              <Text fontStyle="BM" className="w-full text-right font-spaceMono!">
                {word("boxShop.shoppingView.newOrder.table.header.price")}
              </Text>
            </div>
            {newOrder.map((orderItem) => (
              <Fragment key={orderItem.product.id}>
                <div key={orderItem.product.id + "-title"} className="col-span-6">
                  <Text fontStyle="BS" className="w-full text-center font-spaceMono! text-textOnSurface-subtle">
                    {orderItem.product.title}
                  </Text>
                </div>
                <div
                  key={orderItem.product.id + "-quantity"}
                  className="col-span-2 flex flex-row items-center justify-between"
                >
                  {orderItem.quantity > 1 ? (
                    <Button
                      variant="ghost"
                      size="iconSm"
                      onClick={() => {
                        handleSubtractItem(orderItem)
                      }}
                      className="text-textOnSurface-subtle"
                    >
                      <MinusIcon />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="iconSm"
                      onClick={() => {
                        handleRemoveItem(orderItem)
                      }}
                      className="text-textOnSurface-subtle"
                    >
                      <Trash2Icon />
                    </Button>
                  )}
                  <Text fontStyle="BS" className="z-10 text-center font-spaceMono! text-textOnSurface-subtle">
                    {orderItem.quantity}
                  </Text>
                  <Button
                    variant="ghost"
                    size="iconSm"
                    onClick={() => {
                      handleAddItem(orderItem.product)
                    }}
                    className="text-textOnSurface-subtle"
                  >
                    <PlusIcon />
                  </Button>
                </div>
                <div key={orderItem.product.id + "-price"} className="col-span-4 flex justify-end">
                  <Text fontStyle="BS" className="w-full text-right font-spaceMono! text-textOnSurface-subtle">
                    {formatCurrency(orderItem.product.price.value, orderItem.product.price.currency, language)}
                  </Text>
                </div>
              </Fragment>
            ))}
          </div>

          {/* NEW ORDER FOOTER */}
          <Activity mode={newOrder.length > 0 ? "visible" : "hidden"}>
            {/* NEW ORDER FOOTER DISCOUNT */}
            <form.AppField name="coupon">
              {(field) => (
                <>
                  <div className="flex w-full justify-end">
                    {field.state.value ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          field.handleChange(null)
                        }}
                      >
                        {word("boxShop.shoppingView.newOrder.discount.remove")}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          field.handleChange({ type: CouponType.PERCENT, value: 0 })
                        }}
                      >
                        {word("boxShop.shoppingView.newOrder.discount.add")}
                      </Button>
                    )}
                  </div>
                  <Activity mode={field.state.value ? "visible" : "hidden"}>
                    <DiscountInput<string, BoxShopSalesFormValues>
                      label={word("boxShop.shoppingView.newOrder.discount.label")}
                      name="coupon"
                      setFieldValue={(_, value) => {
                        field.handleChange(value as BoxShopSalesCouponDto | null)
                        return Promise.resolve()
                      }}
                      getFieldProps={(name) => ({
                        name,
                        value: field.state.value,
                        onChange: () => undefined,
                        onBlur: field.handleBlur,
                      })}
                      currency={currentBox?.stripeAccountCurrency ?? "EUR"}
                      error={<field.Message />}
                    />
                  </Activity>
                </>
              )}
            </form.AppField>

            {/* NEW ORDER FOOTER VAT & TOTAL */}
            <div className="flex flex-col">
              <div className="flex w-full flex-row justify-between">
                <Text fontStyle="BS" className="font-spaceMono!">
                  {word("boxShop.shoppingView.newOrder.vat.label")}
                </Text>
                <Text fontStyle="BS" className="font-spaceMono!">
                  {formatCurrency(VAT, currentBox?.stripeAccountCurrency ?? "EUR", language)}
                </Text>
              </div>
              <div className="flex w-full flex-row justify-between">
                <div className="flex flex-row">
                  <Text fontStyle="TL" className="font-spaceMono!">
                    {word("boxShop.shoppingView.newOrder.total.label")}
                  </Text>
                  <Text fontStyle="LS">{word("boxShop.shoppingView.newOrder.total.tax")}</Text>
                </div>

                <Text fontStyle="TL" className="font-spaceMono!">
                  {formatCurrency(totalPrice, currentBox?.stripeAccountCurrency ?? "EUR", language)}
                </Text>
              </div>
            </div>

            {/* PAYMENT METHOD */}
            <div className="flex flex-col gap-4 rounded-3xl bg-neutral-100 p-4">
              <form.AppField
                name="payment"
                listeners={{
                  onChange: ({ value }) => {
                    // A payment link has no out-of-band method, reset it when switching.
                    if (value === BoxShopSalesPayement.LINK) {
                      form.setFieldValue("outOfBand", null)
                    }
                  },
                }}
              >
                {(paymentField) => (
                  <>
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-row">
                        <Text fontStyle="LM" className="font-spaceMono!">
                          {word("boxShop.shoppingView.newOrder.payment.label").toUpperCase()}
                        </Text>
                        <Text fontStyle="LM" className="text-red-500">
                          {" "}
                          *
                        </Text>
                      </div>
                      <ChipGroup spacing={2} variant="emphasisLow" value={[paymentField.state.value]}>
                        <Chip
                          value={BoxShopSalesPayement.DIRECT}
                          variant="emphasisLow"
                          size="md"
                          aria-label="Payement direct"
                          aria-pressed={paymentField.state.value === BoxShopSalesPayement.DIRECT}
                          onClick={() => {
                            paymentField.handleChange(BoxShopSalesPayement.DIRECT)
                          }}
                        >
                          <StoreIcon />
                          {word("boxShop.shoppingView.newOrder.payment.direct")}
                        </Chip>
                        <Chip
                          value={BoxShopSalesPayement.LINK}
                          variant="emphasisLow"
                          size="md"
                          aria-label="Payment link"
                          aria-pressed={paymentField.state.value === BoxShopSalesPayement.LINK}
                          onClick={() => {
                            paymentField.handleChange(BoxShopSalesPayement.LINK)
                          }}
                        >
                          <ScanQrCodeIcon />
                          {word("boxShop.shoppingView.newOrder.payment.link")}
                        </Chip>
                      </ChipGroup>
                    </div>
                    {paymentField.state.value === BoxShopSalesPayement.DIRECT ? (
                      <form.AppField name="outOfBand">
                        {(outOfBandField) => (
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-row">
                              <Text fontStyle="LM" className="font-spaceMono!">
                                {word("boxShop.shoppingView.newOrder.method.label").toUpperCase()}
                              </Text>
                              <Text fontStyle="LM" className="text-red-500">
                                {" "}
                                *
                              </Text>
                            </div>
                            <ChipGroup
                              spacing={2}
                              variant="emphasisLow"
                              value={outOfBandField.state.value ? [outOfBandField.state.value] : []}
                              onBlur={() => {
                                outOfBandField.handleBlur()
                              }}
                            >
                              <Chip
                                value={OutOfBandTransactionStatus.CARD}
                                variant="emphasisLow"
                                size="md"
                                aria-label="Credit card"
                                aria-pressed={outOfBandField.state.value === OutOfBandTransactionStatus.CARD}
                                onClick={() => {
                                  outOfBandField.handleChange(
                                    outOfBandField.state.value === OutOfBandTransactionStatus.CARD
                                      ? null
                                      : OutOfBandTransactionStatus.CARD,
                                  )
                                }}
                              >
                                {word("boxShop.shoppingView.newOrder.method.creditCard")}
                              </Chip>
                              <Chip
                                value={OutOfBandTransactionStatus.CASH}
                                variant="emphasisLow"
                                size="md"
                                aria-label="Cash"
                                aria-pressed={outOfBandField.state.value === OutOfBandTransactionStatus.CASH}
                                onClick={() => {
                                  outOfBandField.handleChange(
                                    outOfBandField.state.value === OutOfBandTransactionStatus.CASH
                                      ? null
                                      : OutOfBandTransactionStatus.CASH,
                                  )
                                }}
                              >
                                {word("boxShop.shoppingView.newOrder.method.cash")}
                              </Chip>
                              <Chip
                                value={OutOfBandTransactionStatus.CHECK}
                                variant="emphasisLow"
                                size="md"
                                aria-label="Check"
                                aria-pressed={outOfBandField.state.value === OutOfBandTransactionStatus.CHECK}
                                onClick={() => {
                                  outOfBandField.handleChange(
                                    outOfBandField.state.value === OutOfBandTransactionStatus.CHECK
                                      ? null
                                      : OutOfBandTransactionStatus.CHECK,
                                  )
                                }}
                              >
                                {word("boxShop.shoppingView.newOrder.method.check")}
                              </Chip>
                            </ChipGroup>
                            <outOfBandField.Message />
                          </div>
                        )}
                      </form.AppField>
                    ) : null}
                  </>
                )}
              </form.AppField>
              <div className="flex flex-col">
                {/* SEARCH CUSTOMER */}
                <form.AppField name="userId">
                  {(field) => (
                    <field.Field>
                      <field.Label>{word("boxShop.shoppingView.newOrder.customer.label")}</field.Label>
                      <field.Content>
                        <field.SearchUser
                          buttonTextSearch={word("boxShop.shoppingView.newOrder.customer.search.button")}
                          searchDescriptionText={word("boxShop.shoppingView.newOrder.customer.search.description")}
                          tooltipContentProps={{ className: "max-w-50", side: "left" }}
                        />
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              </div>
            </div>
            {/* NEW ORDER FOOTER SELL BUTTON */}
            {canSaleItem && (
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => void form.handleSubmit()}
                disabled={isPendingCreateSale}
              >
                {isPendingCreateSale && <LoaderCircleIcon className="animate-spin" />}
                {word("boxShop.shoppingView.newOrder.sell")}
              </Button>
            )}
          </Activity>
        </div>
      </div>
      <BoxShopSalesDialog
        saleId={saleId ?? ""}
        orderItems={newOrder}
        coupon={couponValue}
        userId={userIdValue}
        payment={paymentValue}
        checkoutUrl={salesDialogCheckoutUrl}
        open={salesDialogOpen}
        onOpenChange={(open) => {
          setSalesDialogOpen(open)
          if (!open) {
            handleResetOrder()
          }
        }}
        handleResetOrder={handleResetOrder}
      />
    </>
  )
}
